/**
 * AURA CSS Injection Engine
 * Injects scoped style overrides into host document
 */

import { Logger } from './logger';

interface StyleSheet {
  id: string;
  css: string;
  priority: number;
}

export class CssInjector {
  private stylesheets: Map<string, StyleSheet> = new Map();
  private styleElements: Map<string, HTMLStyleElement> = new Map();
  private adoptedSheet: CSSStyleSheet | null = null;
  private supportsAdoptedStylesheets: boolean;
  private logger = new Logger('CssInjector');

  constructor() {
    // Deliberately disabled: mutating document.adoptedStyleSheets replaces
    // the host page's FrozenArray, which can corrupt CSS-in-JS frameworks
    // (Lit, Emotion, etc.) that cache a reference to it.
    this.supportsAdoptedStylesheets = false;
  }

  /**
   * Initialize the CSS injector (used by engine).
   * All CSS is injected via &lt;style&gt; elements to avoid mutating
   * document.adoptedStyleSheets which can break host-page frameworks.
   */
  init(): void {
    this.logger.debug('CSS injector initialized (style-element mode)');
  }

  /**
   * Inject a style element with a given id and CSS content
   */
  inject(id: string, css: string): void {
    const existing = this.styleElements.get(id);
    if (existing && existing.parentNode) {
      existing.textContent = css;
      this.logger.debug(`Updated style: ${id}`);
      return;
    }

    // Stale reference (element removed from DOM externally) -- discard it
    if (existing) {
      this.styleElements.delete(id);
    }

    const target = document.head ?? document.documentElement;
    if (!target) {
      this.logger.warn(`Cannot inject style "${id}": no DOM target available`);
      return;
    }

    const styleEl = document.createElement('style');
    styleEl.setAttribute('data-aura-shield', id);
    styleEl.textContent = css;
    target.appendChild(styleEl);
    this.styleElements.set(id, styleEl);
    this.logger.debug(`Injected style: ${id}`);
  }

  /**
   * Remove a style element by id
   */
  remove(id: string): void {
    const styleEl = this.styleElements.get(id);
    if (styleEl?.parentNode) {
      styleEl.parentNode.removeChild(styleEl);
    }
    this.styleElements.delete(id);
    this.logger.debug(`Removed style: ${id}`);
  }

  /**
   * Remove all injected style elements
   */
  removeAll(): void {
    this.styleElements.forEach((styleEl) => {
      if (styleEl.parentNode) {
        styleEl.parentNode.removeChild(styleEl);
      }
    });
    this.styleElements.clear();
    this.logger.debug('Removed all styles');
  }

  /**
   * Check if a style element with the given id exists
   */
  has(id: string): boolean {
    return this.styleElements.has(id);
  }

  /**
   * Generate high-specificity selector by wrapping with :where(html) body
   */
  highSpecificitySelector(selector: string): string {
    return selector
      .split(',')
      .map((s) => `:where(html) body ${s.trim()}`)
      .join(', ');
  }

  /**
   * Generate a CSS declaration with !important
   */
  importantRule(property: string, value: string): string {
    return `${property}: ${value} !important;`;
  }

  /**
   * Generate CSS rules from a selector-to-declarations map
   */
  generateRules(
    rules: Record<string, Record<string, string>>
  ): string {
    return Object.entries(rules)
      .map(([selector, props]) => {
        const declarations = Object.entries(props)
          .map(([prop, val]) => `  ${prop}: ${val};`)
          .join('\n');
        return `${selector} {\n${declarations}\n}`;
      })
      .join('\n\n');
  }

  /**
   * Add or update a stylesheet (used by feature modules)
   */
  setStylesheet(id: string, css: string, priority = 0): void {
    this.stylesheets.set(id, { id, css, priority });
    this.applyStyles();
    this.logger.debug(`Set stylesheet: ${id}`, { priority });
  }

  /**
   * Remove a stylesheet by id
   */
  removeStylesheet(id: string): boolean {
    const removed = this.stylesheets.delete(id);
    if (removed) {
      this.applyStyles();
      this.logger.debug(`Removed stylesheet: ${id}`);
    }
    return removed;
  }

  /**
   * Check if a stylesheet exists
   */
  hasStylesheet(id: string): boolean {
    return this.stylesheets.has(id);
  }

  /**
   * Clear all stylesheets
   */
  clear(): void {
    this.stylesheets.clear();
    this.applyStyles();
    this.logger.debug('Cleared all stylesheets');
  }

  /**
   * Apply all stylesheets to the document.
   * Uses adoptedStyleSheets when available, falls back to a <style> element.
   */
  private applyStyles(): void {
    const sorted = Array.from(this.stylesheets.values()).sort(
      (a, b) => a.priority - b.priority
    );
    const combinedCss = sorted
      .map((s) => `/* ${s.id} */\n${s.css}`)
      .join('\n\n');

    if (this.supportsAdoptedStylesheets && this.adoptedSheet) {
      try {
        this.adoptedSheet.replaceSync(combinedCss);
        return;
      } catch (error) {
        this.logger.error('Failed to update adopted stylesheet', error);
        this.supportsAdoptedStylesheets = false;
      }
    }

    this.applyViaStyleElement(combinedCss);
  }

  private applyViaStyleElement(css: string): void {
    this.inject('aura-combined-styles', css);
  }

  /**
   * Destroy the injector and clean up all resources
   */
  destroy(): void {
    this.removeAll();
    this.stylesheets.clear();
    this.logger.debug('Destroyed CSS injector');
  }

  /**
   * Generate high-specificity selector (static)
   */
  static highSpecificity(selector: string): string {
    return `:where(html) body ${selector}`;
  }

  /**
   * Generate CSS with !important (static)
   */
  static important(property: string, value: string): string {
    return `${property}: ${value} !important;`;
  }
}

// Singleton instance
export const cssInjector = new CssInjector();
